/**
 * Individual addresses allowed to sign in **in addition to** the company
 * `allowedEmailDomain`. Use this to grant access to a handful of people
 * outside the domain (contractors, partners) without opening login up to
 * every Google account.
 *
 * `REACT_APP_ALLOWED_EMAILS`: comma-separated addresses, case- and
 * whitespace-insensitive (e.g. `a@gmail.com, b@partner.io`). Unset / empty
 * means "no extra addresses" — only the domain applies.
 *
 * Same opt-in convention as `REACT_APP_ALLOWED_EMAIL_DOMAIN` and
 * `REACT_APP_ZONE_EDITOR_EMAILS`: baked into the bundle at build time.
 *
 * ⚠️ This is a UI gate, not a security boundary — the bundle is public and
 * the backend issues a token to any valid Google account. It controls who
 * this app lets *through the login screen*, not who the API trusts.
 */
const parseAllowedEmails = (raw: string): Set<string> =>
  new Set(
    raw
      .split(',')
      .map((token) => token.trim().toLowerCase())
      .filter(Boolean)
  );

const ALLOWED_EMAILS = parseAllowedEmails(
  process.env.REACT_APP_ALLOWED_EMAILS || ''
);

export const APP_CONFIG = {
  name: 'Dash Logistics',
  shortName: 'Dash',
  subtitle: 'Logistics Platform',
  homeRoute: '/invoices',
  allowedEmailDomain: process.env.REACT_APP_ALLOWED_EMAIL_DOMAIN || '',
  allowedEmails: ALLOWED_EMAILS,
  loginTitle: 'Login to your workspace',
  loginDescription:
    'Sign in with your Dash account to continue to the Logistics platform.',
  copyright: '© 2026 Dash Logistics. All rights reserved',
};

/** True when `email` is on the individual allowlist (domain-independent). */
export const isAllowedEmail = (email: string | null | undefined): boolean =>
  !!email && APP_CONFIG.allowedEmails.has(email.trim().toLowerCase());

export const getAllowedDomainLabel = () => {
  if (!APP_CONFIG.allowedEmailDomain) {
    return 'Any Google account can be used until you add your own restriction.';
  }

  const base = `Only ${APP_CONFIG.allowedEmailDomain} accounts are allowed`;
  if (APP_CONFIG.allowedEmails.size > 0) {
    return `${base}, plus a few individually approved addresses.`;
  }
  return `${base}.`;
};
